import { Box, Chip, debounce, Grid, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { setData, setServiceFunction } from "./../../../../store/formSlice";
import FormTextField from "./../../../../Controllers/Input/index";
import service from "./../../helpers/GifService";
import { debounceDuration } from "./../../siteMap/navigations/helpers";
import { canEditSelector } from "../../../../store/applicantsSlice";
import FormAutoComplete from "../../../../Controllers/AutoComplete";
import { useLoading } from "../../../../providers/loadingProvider";
import { useLocation } from "react-router";
import {
  proposalSelector,
  setProposalNumberOfYears,
} from "../../../../store/proposalSlice";
import { PRE_PROPOSAL_STATUS } from "../../Constants";
const pageName = "General Information";

const TITLE = "title";
const RESEARCH_AREA_DESCRIPTION = "researchAreaDescription";
const PROJECT_PERIOD = "numberOfYear";
const KEY_WORDS = "keyWord";
const RESEARCH_AREA = "researchAreaId";
const RESEARCH_SUB_AREA = "researchSubAreaId";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },

  tagsWrapper: {
    minHeight: 40,
    display: "flex",
    justifyContent: "flex-start",
    flexWrap: "wrap",
    "& > *": {
      margin: theme.spacing(0.5),
    },
  },
}));

const getSubareas = async (areaId) => {
  var areas = await service.getResearchSubAreas({ areaId });
  const mapped = areas.map((researchArea) => ({
    id: researchArea.researchSubAreaId,
    label: researchArea.name,
  }));
  return mapped;
};

const getAreas = async () => {
  var areas = await service.getResearchAreas();
  const mapped = areas.map((researchArea) => ({
    id: researchArea.researchAreaId,
    label: researchArea.name,
  }));
  return mapped;
};
export default function GeneralInformation() {
  const classes = useStyles();
  const methods = useForm();
  const loading = useLoading();
  const dispatch = useDispatch();
  const location = useLocation();
  const canEdit = useSelector(canEditSelector);
  const proposal = useSelector(proposalSelector);
  const { watch, setValue, getValues, formState } = methods;
  const [researchAreasOptions, setResearchAreasOptions] = useState([]);
  const [yearsOptions, setYearsOptions] = useState([]);
  const [chips, setChips] = useState([]);
  const [researchSubAreasOptions, setResearchSubAreasOptions] = useState([]);
  const [proposalId, setProposalId] = useState();
  const isPreProposal = useMemo(
    () => proposal?.status === PRE_PROPOSAL_STATUS,
    [proposal]
  );
  const focus = new URLSearchParams(location.search).get("focus");
  const watchAll = watch();

  useEffect(() => {
    dispatch(setServiceFunction(`updateProposal`));
  }, [dispatch]);

  const debouncedSetData = useMemo(
    () =>
      debounce(() => {
        const dirty = Object.keys(formState.dirtyFields);
        const values = getValues(dirty);
        if (dirty.length === 0) return;

        if (dirty.includes("numberOfYear") && values[PROJECT_PERIOD]) {
          dispatch(setProposalNumberOfYears(values[PROJECT_PERIOD]));
        }
        const proposalData = {
          id: proposalId,
          title: values[TITLE],
          researchAreaDescription: values[RESEARCH_AREA_DESCRIPTION],
          numberOfYear: values[PROJECT_PERIOD],
          keyWord: values[KEY_WORDS],
          researchAreaId: values[RESEARCH_AREA],
          researchSubAreaId: values[RESEARCH_SUB_AREA],
        };

        dispatch(
          setData({
            proposal: proposalData,
          })
        );
      }, debounceDuration),
    [dispatch, formState.dirtyFields, proposalId, getValues]
  );

  // when a  field changes set its value in the store
  useEffect(() => {
    debouncedSetData();
  }, [debouncedSetData, watchAll]);

  const keyWordsWatcher = watch("keyords");
  const areaWatcher = watch(RESEARCH_AREA);

  useEffect(() => {
    if (!keyWordsWatcher) return [];

    const tags = keyWordsWatcher.split(/[ ,]/).filter((c) => c);
    setChips([...new Set(tags)]);
  }, [keyWordsWatcher]);

  useEffect(() => {
    if (!areaWatcher) {
      setResearchSubAreasOptions([]);
      return;
    }
    getSubareas(areaWatcher).then(setResearchSubAreasOptions);
  }, [areaWatcher]);

  useEffect(() => {
    const setProposal = async () => {
      const loadingId = loading.addLoading("Loading Proposal Data");
      const proposalData = await service.getProposalData();
      const {
        id,
        title,
        researchAreaDescription,
        numberOfYear,
        keyWord,
        researchAreaId,
        researchSubAreaId,
        maximunNumberOfYears,
      } = proposalData;

      const areasP = getAreas();
      let subAreasP = [];
      if (researchAreaId !== null) {
        subAreasP = getSubareas(researchAreaId);
      }
      const [areas, subAreas] = await Promise.all([areasP, subAreasP]);
      setResearchAreasOptions(areas);
      setResearchSubAreasOptions(subAreas);
      const yearsOptions = [...Array(maximunNumberOfYears).keys()].map((i) => ({
        id: i + 1,
        label: `${i + 1}`,
      }));
      setYearsOptions(yearsOptions);
      setProposalId(id);
      loading.removeLoading(loadingId);
      setValue(TITLE, title || ""); 
      setValue(RESEARCH_AREA_DESCRIPTION, researchAreaDescription || "");
      setValue(PROJECT_PERIOD, numberOfYear || "");
      setValue(KEY_WORDS, keyWord || "");
      setValue(RESEARCH_AREA, researchAreaId || "");
      setValue(RESEARCH_SUB_AREA, researchSubAreaId || "");
  };
    setProposal();
  }, [loading, setValue]);

  return (
    <FormProvider {...methods} disabled>
      <form>
        <Grid container spacing={2}>
          <Grid item>
            <Typography variant="subtitle2">{pageName}</Typography>
          </Grid>
          <Grid item>
            <InfoOutlinedIcon color="primary" />
          </Grid>
          <Grid item xs={12}>
            <FormTextField
              autoFocus={focus === TITLE}
              name={TITLE}
              label="Application Title"
              helperText="145 Characters"
              disabled={!canEdit || !isPreProposal}
            />
          </Grid>
          <Grid item xs={4}>
            <FormAutoComplete
              autoFocus={focus === RESEARCH_AREA}
              name={RESEARCH_AREA}
              label="Research Area"
              options={researchAreasOptions}
              disabled={!canEdit}
            />
          </Grid>
          <Grid item xs={4}>
            <FormAutoComplete
              autoFocus={focus === RESEARCH_SUB_AREA}
              name={RESEARCH_SUB_AREA}
              label="Research Sub Area"
              options={researchSubAreasOptions}
              disabled={!canEdit || researchSubAreasOptions.length === 0}
            />
          </Grid>
          <Grid item xs={4}>
            <FormAutoComplete
              autoFocus={focus === PROJECT_PERIOD}
              name={PROJECT_PERIOD}
              label="Project Period"
              options={yearsOptions}
              helperText="number of years"
            />
          </Grid>
          <Grid item xs={12}>
            <FormTextField
              autoFocus={focus === RESEARCH_AREA_DESCRIPTION}
              name={RESEARCH_AREA_DESCRIPTION}
              label="Research Area Description"
              multiline
              rows={6}
              disabled={!canEdit}
            />
          </Grid>
          <Grid item xs={12}>
            <FormTextField
              name={KEY_WORDS}
              autoFocus={focus === KEY_WORDS}
              label="Key Words"
              helperText="Maximum 150 Characters, separated with commas or spaces"
              disabled={!canEdit}
            />
            <Box className={classes.tagsWrapper}>
              {chips.map((c, index) => (
                <Chip label={c} key={index} />
              ))}
            </Box>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
}
